// The notes corpus: an enrolled member's whole set of note envelopes in
// IndexedDB, drained by cursor, reconciled against the manifest, removed on
// logout. Plain ES modules, no Vue; the Hub wires it in part 2 of #1548 (see
// README.md in this directory). Nothing here decrypts.
import { createCorpusStore } from './corpus';
import { createDrainer } from './drain';
import { createReconciler } from './reconcile';

export {
  CORPUS_DB_PREFIX,
  CORPUS_DB_VERSION,
  CORPUS_REGISTRY_KEY,
  META_STORE,
  NOTES_STORE,
  corpusDbName,
  emptyMeta,
  isCorpusDbName,
  noteScopeOf,
  scopeKey,
} from './schema';
export { sameInstant } from './reconcile';
export { isQuotaError } from './idb';

/**
 * Build a corpus bound to an IndexedDB and a localStorage. Both default to the
 * window's, resolved at call time, so specs and the Hub seam can inject their
 * own and a page with neither is a quiet no-op.
 */
export function createNotesCorpus(deps = {}) {
  const store = createCorpusStore(deps);
  const drainer = createDrainer(store);
  const reconciler = createReconciler(store);
  return {
    open: store.open,
    close: store.close,
    closeAll: store.closeAll,
    getMeta: store.getMeta,
    listNotes: store.listNotes,
    listRecords: store.listRecords,
    getNote: store.getNote,
    resetScope: store.resetScope,
    removeCorpus: store.removeCorpus,
    // The write path (#1548 part-2 step 5). Reconciliation used these from the
    // inside; a member's own add, edit and delete need them too, or the corpus
    // is a refetch behind their last keystroke and a reopen paints without it.
    putNote: store.putNote,
    deleteNotes: store.deleteNotes,
    removeAllCorpora: store.removeAllCorpora,
    fetchNextPage: drainer.fetchNextPage,
    drain: drainer.drain,
    stop: drainer.stop,
    reconcile: reconciler.reconcile,
  };
}

/** The app's corpus, over window.indexedDB and window.localStorage. */
export const notesCorpus = createNotesCorpus();

/**
 * Logout: remove every member's corpus on this device. Bounded, because every
 * sign-out path blocks on it before the redirect and a deletion another
 * context is blocking must not hold sign-out up; a corpus that survives is
 * removed on the next sign-out. Resolves to the database names removed; never
 * rejects. A no-op when there is no IndexedDB or no corpus (plaintext members).
 */
export function removeAllNotesCorpora({ timeoutMs = 2000 } = {}) {
  return new Promise(resolve => {
    let settled = false;
    const finish = value => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => finish([]), timeoutMs);
    notesCorpus.removeAllCorpora().then(finish, () => finish([]));
  });
}
